// Undo and redo as guarded inverse writes: each change restores only what
// still is what this tab made it (docs/architecture/maps.md, "Undo and redo").
import type { LiveGrid, LiveScene } from "../types/generated/live";
import { layCell, type Terrain, terrainOf } from "./cuts";
import { type Edges, edgesOf, markOf, normalizeEdges } from "./edges";
import { type Fog, fogRows, normalizeRows } from "./fog";
import { geometryWrite, sceneTerrain } from "./geometry";
import { settle } from "./settle";
import { canMove, isVisible } from "./tokens";

type Values = Record<string, unknown>;
type Collection = "tokens" | "drawings" | "notes";
// [col, row, before, after], one code per cell or edge.
export type CellPatch = [number, number, string, string];
type FogAll = { default: string; rows: string[] };
export type Change =
	// `names`: a token's shown name before and after a rename, which the
	// server derives, for the view until the echo.
	// `kind` and `gesture`: a drawing's kind and what made its points change, which name its step; a token's z carries "front" or "back".
	| { type: "update"; collection: Collection; id: string; before: Values; after: Values; names?: [unknown, unknown]; kind?: string; gesture?: string }
	// `combat`: what the server answered when it removed a token that stood for a combatant, which the undo sends back.
	| { type: "create" | "remove"; collection: Collection; id: string; record: Values; combat?: Values }
	// `cuts`, `corners` and `borders`: the cut, the corner floor and the border of each cell the step touched, with `cells` listing the same cells: a touched cell records its floor, its cut, its corner and its border, unchanged ones included, and all are guarded together with the diagonal mark `d` it changed in the same write. `borders` is only there when the scene or the step holds one.
	| { type: "terrain"; cells: CellPatch[]; cuts?: CellPatch[]; corners?: CellPatch[]; borders?: CellPatch[]; d?: CellPatch[] }
	| { type: "fog"; default: string; cells: CellPatch[] }
	// Several pieces' changes made as one step (a group move, hide, snap or removal): each guarded and reversed on its own.
	| { type: "group"; changes: Change[] }
	| { type: "fogAll"; before: FogAll; after: FogAll }
	// The grid shown or hidden (the G key); the grid's other keys are never part of a step.
	| { type: "grid"; before: boolean; after: boolean }
	// An edge write that settled a cell whole carries that cell's terrain patches too (`cells`, `cuts`, `corners`, `borders`), undone with its diagonal mark.
	| {
			type: "edges";
			n: CellPatch[];
			w: CellPatch[];
			d?: CellPatch[];
			cells?: CellPatch[];
			cuts?: CellPatch[];
			corners?: CellPatch[];
			borders?: CellPatch[];
	  };
export type Write =
	| { type: "update"; collection: Collection; id: string; attrs: Values; shown?: Values }
	| { type: "create"; collection: Collection; id: string; record: Values; body: Values; combat?: Values }
	| { type: "remove"; collection: Collection; id: string }
	| { type: "scene"; attrs: Values; local: Values };
export type Direction = "undo" | "redo";
type Family = "rows" | "cuts" | "corners" | "borders" | "d";
type Scene = Values & Partial<Pick<LiveScene, "terrain" | "edges">> & { grid?: Pick<LiveGrid, "kind"> & Partial<Pick<LiveGrid, "visible">>; fog?: Fog };
type View = { scene?: Scene; tokens?: Record<string, Values>; drawings?: Record<string, Values>; notes?: Record<string, Values> };
type Viewer = { manage: boolean; accountId: string | null };

// What a resize or a regrid changes under the steps stored by cell; the history is kept only while it holds.
export function shapeKey(scene: Scene | undefined) {
	if (!scene) return null;
	const grid = (scene.grid ?? {}) as Values;
	return [scene.width_px, scene.height_px, grid.cell_w, grid.cell_h, grid.offset_x, grid.offset_y, grid.hex_parity].join("|");
}

// What `create` takes back for a removed record.
const CREATE_KEYS: Record<Collection, string[]> = {
	tokens: [
		"kind",
		"character_id",
		"retainer_id",
		"monster_id",
		"prefab",
		"label",
		"x",
		"y",
		"size",
		"span",
		"rotation",
		"z",
		"hidden",
		"locked",
		"snap",
		"ring_color",
	],
	drawings: ["kind", "points", "color", "width", "style", "z", "text"],
	notes: ["x", "y", "mark", "text", "tags"],
};
// What a person changes on a record, so what tells whether it is still as they left it.
const GUARDED: Record<Collection, string[]> = {
	tokens: ["x", "y", "size", "span", "rotation", "label", "hidden", "locked", "snap", "ring_color"],
	drawings: ["points", "text", "color", "width", "style"],
	notes: ["x", "y", "mark", "text", "tags"],
};
// A write that changes one of these is anchored at a corner or an edge, so it
// moves the centre with it.
const SHAPE_KEYS = ["size", "span"];
const DEFAULTS: Values = { size: 1, span: null, rotation: 0, label: null, hidden: false, locked: false, snap: true, ring_color: null };
const EPSILON = 0.01;

export function updateChange(collection: Collection, record: Values, attrs: Values, gesture?: string): Change {
	const before = Object.fromEntries(Object.keys(attrs).map((key) => [key, record[key] ?? DEFAULTS[key] ?? null]));
	const change: Change = { type: "update", collection, id: record.id as string, before, after: { ...attrs } };
	if (collection === "tokens" && "label" in attrs) change.names = [record.name ?? null, String(attrs.label ?? "").trim() || null];
	if (collection === "drawings") change.kind = record.kind as string;
	if (gesture && (collection === "drawings" || "z" in attrs)) change.gesture = gesture;
	return change;
}

// Consecutive renames: the first one's before, the last one's after; null
// when another writer's change came between them.
export function mergeChanges(earlier: Change, later: Change): Change | null {
	if (earlier.type !== "update" || later.type !== "update") return null;
	if (!Object.keys(later.before).every((key) => !(key in earlier.after) || same(key, earlier.after[key], later.before[key], later.collection))) return null;
	const names = earlier.names && later.names ? { names: [earlier.names[0], later.names[1]] as [unknown, unknown] } : {};
	return { ...later, before: { ...later.before, ...earlier.before }, after: { ...earlier.after, ...later.after }, ...names };
}

// A board write to the scene that is a step: a terrain stroke, a fog gesture,
// Revelar or Esconder tudo, an edge gesture, the grid shown or hidden. Settings give null.
export function sceneChange(scene: Scene, attrs: Values): Change | null {
	const terrain = attrs.terrain as { rows?: string[]; cuts?: string[]; corners?: string[]; borders?: string[] } | undefined;
	const fog = attrs.fog as { default?: string; rows?: string[]; enabled?: boolean } | undefined;
	const edges = attrs.edges as Edges | undefined;
	const grid = attrs.grid as Values | undefined;
	let change: Change | null = null;
	if (grid) {
		const keys = Object.keys(grid);
		return keys.length === 1 && typeof grid.visible === "boolean" ? { type: "grid", before: gridShown(scene), after: grid.visible } : null;
	}
	if (terrain?.rows) change = geometryChange(scene, terrain, edges);
	else if (fog?.default) change = { type: "fogAll", before: fogState(scene.fog), after: { default: fog.default, rows: normalizeRows(fog.rows) } };
	else if (fog?.rows && fog.enabled === undefined)
		change = { type: "fog", default: fogDefault(scene.fog), cells: patches(normalizeRows(fogRows(scene.fog)), fog.rows) };
	else if (edges) change = geometryChange(scene, undefined, edges);
	return change && !isEmpty(change) ? change : null;
}

// A terrain write and an edge write are one step when they travel together: a stroke that wrote a wall on a diagonal, or
// an edge gesture that left a cut cell whole. It is a terrain step unless an edge `n` or `w` changed or the terrain did not.
// The "after" is what the scene holds once the write is applied, so the joint step (settle.ts) runs on it first: a cell the
// write left whole has no diagonal mark. Every cell whose floor, cut, corner or border changed, or that a mark's change settled,
// records all of floor, cut, corner, border and mark, unchanged `.` ones included, so the step is guarded on, and restores,
// the cell's whole tuple. A cell where only the mark changed records the mark and its cut, which guards no more than a
// reversal needs: the mark itself, and that the cell is still cut.
function geometryChange(
	scene: Scene,
	terrain: { rows?: string[]; cuts?: string[]; corners?: string[]; borders?: string[] } | undefined,
	edges: Edges | undefined,
): Change {
	const current = edgesOf(scene.edges);
	const [n, w] = (["n", "w"] as const).map((key) => (edges ? patches(current[key] ?? [], edges[key] ?? []) : []));
	const before = sceneTerrain(scene.terrain);
	const written = terrain?.rows ? terrainOf(terrain.rows, terrain.cuts, terrain.corners, terrain.borders) : before;
	const settled = settle(written, edges ?? current);
	const after = sceneTerrain(settled.terrain);
	const lines = (value: Terrain, d: string[]) => ({ rows: value.rows, cuts: value.cuts, corners: value.corners ?? [], borders: value.borders ?? [], d });
	const [from, to] = [lines(before, current.d ?? []), lines(after, settled.edges.d ?? [])];
	const cells = new Map<string, [number, number]>();
	for (const family of ["rows", "cuts", "corners", "borders"] as const) {
		for (const [col, row] of patches(from[family], to[family])) cells.set(`${col},${row}`, [col, row]);
	}
	const marks = new Map<string, [number, number]>();
	for (const [col, row] of patches(from.d, to.d)) if (!cells.has(`${col},${row}`)) marks.set(`${col},${row}`, [col, row]);
	const inOrder = (list: Map<string, [number, number]>) => [...list.values()].sort((a, b) => a[1] - b[1] || a[0] - b[0]);
	const whole = inOrder(cells);
	const record = (family: Family, list: [number, number][]) =>
		list.map(([col, row]): CellPatch => [col, row, codeAt(from[family], col, row), codeAt(to[family], col, row)]);
	const bordered = Boolean(from.borders.length || to.borders.length);
	const dCells = inOrder(new Map([...cells, ...marks]));
	const tuple = dCells.length
		? {
				...(whole.length
					? { cells: record("rows", whole), corners: record("corners", whole), ...(bordered ? { borders: record("borders", whole) } : {}) }
					: {}),
				cuts: record("cuts", dCells),
				d: record("d", dCells),
			}
		: null;
	if (tuple && whole.length && !n.length && !w.length) return { type: "terrain", ...tuple } as Change;
	return { type: "edges", n, w, ...(tuple ?? {}) };
}

export function createBody(collection: Collection, record: Values): Values {
	return Object.fromEntries(CREATE_KEYS[collection].filter((key) => record[key] != null).map((key) => [key, record[key]]));
}

// The write that takes `change` back (undo) or does it again (redo), and the
// change as far as it applied, for the other stack; null when nothing of it
// still holds.
export function reverse(change: Change, view: View, direction: Direction, viewer: Viewer): { write: Write; change: Change } | null {
	const undo = direction === "undo";
	switch (change.type) {
		case "update":
			return reverseUpdate(change, view, undo, viewer);
		case "create":
		case "remove":
			return (change.type === "create") === undo ? removeIfUnchanged(change, view) : createIfAbsent(change, view);
		case "terrain":
		case "edges":
			return reverseGeometry(change, view.scene, undo);
		case "fog":
			return reverseFog(change, view.scene, undo);
		case "fogAll":
			return reverseFogAll(change, view.scene, undo);
		case "grid":
			return reverseGrid(change, view.scene, undo);
		case "group":
			return null;
	}
}

// What taking a step back (or doing it again) writes: one write per change of a group, those that still hold, and the step as far as
// it applied (one change left is that change alone). Null when none does.
export function reverseAll(
	change: Change,
	view: View,
	direction: Direction,
	viewer: Viewer,
): { steps: { write: Write; change: Change }[]; change: Change } | null {
	if (change.type !== "group") {
		const one = reverse(change, view, direction, viewer);
		return one && { steps: [one], change: one.change };
	}
	const steps = change.changes.flatMap((each) => reverse(each, view, direction, viewer) ?? []);
	if (steps.length < 2) return steps.length ? { steps, change: steps[0].change } : null;
	return { steps, change: { type: "group", changes: steps.map((step) => step.change) } };
}

// The record or scene field a change touches.
export function recordKey(change: Change): string {
	if ("collection" in change) return `${change.collection}:${change.id}`;
	return `scene:${change.type === "fogAll" ? "fog" : change.type}`;
}

// Every record or scene field a step touches: a group's are its changes' together.
export function recordKeys(change: Change): string[] {
	return change.type === "group" ? change.changes.flatMap(recordKeys) : [recordKey(change)];
}

// The key of the history's label (campaigns.map_scenes.show.history.actions).
export function actionOf(change: Change): string {
	switch (change.type) {
		case "group":
			return `${actionOf(change.changes[0])}_group`;
		case "update":
			if (change.collection === "notes") return "x" in change.after || "y" in change.after ? "move_note" : "edit_note";
			return change.collection === "drawings" ? drawingAction(change) : tokenAction(change);
		case "create":
			if (change.collection === "notes") return "add_note";
			if (change.collection === "tokens") return "place";
			return { text: "write_text", area: "draw_area" }[change.record.kind as string] ?? "draw_line";
		case "remove":
			if (change.collection === "notes") return "delete_note";
			if (change.collection === "tokens") return "remove";
			return change.record.kind === "text" ? "erase_text" : "erase_drawing";
		case "terrain":
			if (change.cuts?.some(([, , before, after]) => before !== after) && !change.cells.some(([, , , after]) => after === ".")) return "cut_diagonal";
			return change.cells.every(([, , , after]) => after === ".") ? "erase_terrain" : "paint_terrain";
		case "fog": {
			const after = change.cells[0]?.[3];
			const state = after === "r" ? "revealed" : after === "h" ? "hidden" : change.default;
			return state === "revealed" ? "reveal_fog" : "hide_fog";
		}
		case "fogAll":
			return change.after.default === "revealed" ? "reveal_all" : "hide_all";
		case "grid":
			return change.after ? "show_grid" : "hide_grid";
		case "edges": {
			const [, , before, after] = change.n[0] ?? change.w[0] ?? change.d?.[0] ?? [];
			const [was, now] = [markOf(before), markOf(after)];
			if (was && now && was.kind === now.kind) return Boolean(was.secret) === Boolean(now.secret) ? "change_door" : was.secret ? "reveal_mark" : "hide_mark";
			if (after === ".") return "erase_edges";
			return (now && { door: "door", window: "window", slit: "arrow_slit", grate: "grate", wall: "wall" }[now.kind]) || "edges";
		}
	}
}

function drawingAction({ after, kind, gesture }: Extract<Change, { type: "update" }>) {
	const label = kind === "text";
	if ("text" in after) return "edit_text";
	if ("color" in after) return label ? "recolor_text" : "recolor_drawing";
	if ("width" in after) return label ? "resize_text" : "rewidth_drawing";
	if ("style" in after) return "restyle_drawing";
	return { turn: "turn_drawing", resize: "resize_drawing", flip: "flip_drawing" }[gesture ?? ""] ?? "move_drawing";
}

function tokenAction({ before, after, gesture }: Extract<Change, { type: "update" }>) {
	if ("span" in after) return "stretch";
	if ("size" in after) return "resize";
	if ("x" in after || "y" in after) return "move";
	if ("rotation" in after) return "turn";
	if ("label" in after) return "rename";
	if ("hidden" in after) return after.hidden ? "hide" : "show";
	if ("locked" in after) return after.locked ? "lock" : "unlock";
	if ("snap" in after) return after.snap ? "snap_on" : "snap_off";
	if ("z" in after) return (gesture ? gesture === "front" : Number(after.z) > Number(before.z)) ? "raise" : "lower";
	return "recolor";
}

// Guarded all or nothing per unit: {x, y}; a shape write's keys with its
// rotation; each other key alone.
function units(keys: string[]) {
	const shape = keys.some((key) => SHAPE_KEYS.includes(key));
	const together = shape ? [...SHAPE_KEYS, "x", "y", "rotation"] : ["x", "y"];
	const unit = keys.filter((key) => together.includes(key));
	return [...(unit.length ? [unit] : []), ...keys.filter((key) => !together.includes(key)).map((key) => [key])];
}

function reverseUpdate(change: Extract<Change, { type: "update" }>, view: View, undo: boolean, viewer: Viewer) {
	const record = view[change.collection]?.[change.id];
	if (!record) return null;
	if (change.collection === "tokens" && !viewer.manage && !(isVisible(record, viewer) && canMove(record, viewer, view.scene))) return null;
	const [from, to] = undo ? [change.after, change.before] : [change.before, change.after];
	const kept = units(Object.keys(change.after))
		.filter((unit) => unit.every((key) => same(key, record[key], from[key], change.collection)))
		.flat();
	if (!kept.length) return null;
	const pick = (values: Values) => Object.fromEntries(kept.map((key) => [key, values[key]]));
	const names = kept.includes("label") ? change.names : undefined;
	const name = names?.[undo ? 0 : 1];
	const { names: _names, ...rest } = change;
	return {
		write: { type: "update", collection: change.collection, id: change.id, attrs: pick(to), ...(name ? { shown: { name } } : {}) } as Write,
		change: { ...rest, before: pick(change.before), after: pick(change.after), ...(names ? { names } : {}) },
	};
}

function removeIfUnchanged(change: Extract<Change, { record: Values }>, view: View) {
	const present = view[change.collection]?.[change.id];
	if (!present || !GUARDED[change.collection].every((key) => same(key, present[key], change.record[key], change.collection))) return null;
	return { write: { type: "remove", collection: change.collection, id: change.id } as Write, change: { ...change, record: { ...change.record, ...present } } };
}

function createIfAbsent(change: Extract<Change, { record: Values }>, view: View) {
	if (view[change.collection]?.[change.id]) return null;
	const { collection, id, record, combat } = change;
	return { write: { type: "create", collection, id, record, body: createBody(collection, record), ...(combat && { combat }) } as Write, change };
}

// A step that touched the terrain, the diagonal marks or the sides' edges, undone or done again by one write.
function reverseGeometry(change: Extract<Change, { type: "terrain" | "edges" }>, scene: Scene | undefined, undo: boolean) {
	if (!scene) return null;
	const current = edgesOf(scene.edges);
	const terrain = applyUnits(sceneTerrain(scene.terrain), current.d ?? [], change, undo);
	const n = change.type === "edges" ? applyPatches(current.n ?? [], change.n, undo) : null;
	const w = change.type === "edges" ? applyPatches(current.w ?? [], change.w, undo) : null;
	if (!terrain && !n && !w) return null;
	const edges = normalizeEdges({ n: n?.rows ?? current.n ?? [], w: w?.rows ?? current.w ?? [], d: terrain?.d ?? current.d ?? [] }) as Edges;
	const write = geometryWrite(scene, terrain?.terrain ?? sceneTerrain(scene.terrain), edges);
	if (!write) return null;
	const applied: Record<string, CellPatch[]> = {
		cells: terrain?.applied.rows ?? [],
		cuts: terrain?.applied.cuts ?? [],
		corners: terrain?.applied.corners ?? [],
		borders: terrain?.applied.borders ?? [],
		d: terrain?.applied.d ?? [],
	};
	const next: Change = { ...change };
	for (const key of Object.keys(applied)) if (key in change) Object.assign(next, { [key]: applied[key] });
	if (next.type === "edges") Object.assign(next, { n: n?.cells ?? [], w: w?.cells ?? [] });
	return { write: sceneWrite(write.attrs, write.local), change: next };
}

function reverseFog(change: Extract<Change, { type: "fog" }>, scene: Scene | undefined, undo: boolean) {
	if (!scene || fogDefault(scene.fog) !== change.default) return null;
	const applied = applyPatches(normalizeRows(fogRows(scene.fog)), change.cells, undo);
	if (!applied) return null;
	return { write: sceneWrite({ fog: { rows: applied.rows } }, { fog: { ...scene.fog, rows: applied.rows } }), change: { ...change, cells: applied.cells } };
}

function reverseFogAll(change: Extract<Change, { type: "fogAll" }>, scene: Scene | undefined, undo: boolean) {
	const [from, to] = undo ? [change.after, change.before] : [change.before, change.after];
	const current = fogState(scene?.fog);
	if (!scene || current.default !== from.default || current.rows.join("\n") !== from.rows.join("\n")) return null;
	return { write: sceneWrite({ fog: { ...to } }, { fog: { ...scene.fog, ...to } }), change };
}

function gridShown(scene: Scene | undefined) {
	return scene?.grid?.visible !== false;
}

function reverseGrid(change: Extract<Change, { type: "grid" }>, scene: Scene | undefined, undo: boolean) {
	const [from, to] = undo ? [change.after, change.before] : [change.before, change.after];
	if (!scene?.grid || gridShown(scene) !== from) return null;
	return { write: sceneWrite({ grid: { visible: to } }, { grid: { ...scene.grid, visible: to } }), change };
}

// A cell's floor, cut, corner, border and diagonal mark are laid back together, and only where every one still holds what the
// step left, so another writer's change to any of them keeps the whole cell. A mark changed alone is a unit of its own.
function applyUnits(current: Terrain, marks: string[], change: Extract<Change, { type: "terrain" | "edges" }>, undo: boolean) {
	const lines: Record<Family, string[]> = {
		rows: [...current.rows],
		cuts: [...current.cuts],
		corners: [...(current.corners ?? [])],
		borders: [...(current.borders ?? [])],
		d: [...marks],
	};
	const units = new Map<string, Partial<Record<Family, CellPatch>>>();
	const listed: [Family, CellPatch[] | undefined][] = [
		["rows", change.cells],
		["cuts", change.cuts],
		["corners", change.corners],
		["borders", change.borders],
		["d", change.d],
	];
	for (const [family, list] of listed) {
		for (const patch of list ?? []) units.set(`${patch[0]},${patch[1]}`, { ...units.get(`${patch[0]},${patch[1]}`), [family]: patch });
	}
	const applied: Record<Family, CellPatch[]> = { rows: [], cuts: [], corners: [], borders: [], d: [] };
	for (const unit of units.values()) {
		const patches = Object.entries(unit) as [Family, CellPatch][];
		// A unit with no floor patch is a mark alone: its cut only has to be there still, and is never laid back.
		const guardOnly = (family: Family) => family === "cuts" && !unit.rows;
		const holds = ([family, patch]: [Family, CellPatch]) =>
			guardOnly(family) ? codeAt(lines.cuts, patch[0], patch[1]) !== "." : codeAt(lines[family], patch[0], patch[1]) === patch[undo ? 3 : 2];
		if (!patches.every(holds)) continue;
		// A step on a scene with no border records none, so a border another writer gave the cell since is a change too.
		if (unit.rows && !unit.borders && codeAt(lines.borders, unit.rows[0], unit.rows[1]) !== ".") continue;
		// A cell the step recorded as a mark alone must stay one: taking its mark away where both halves are now one floor
		// would make the joint step collapse it, which the step does not describe, so the cell is refused like a failed guard.
		const mark = unit.d?.[undo ? 2 : 3];
		const [col, row] = [unit.d?.[0] ?? 0, unit.d?.[1] ?? 0];
		const corner = codeAt(lines.corners, col, row);
		if (unit.d && !unit.rows && mark === "." && corner !== "." && corner === codeAt(lines.rows, col, row)) continue;
		for (const [family, patch] of patches) {
			if (!guardOnly(family)) layCell(lines[family], patch[0], patch[1], patch[undo ? 2 : 3]);
			applied[family].push(patch);
		}
	}
	if (!Object.values(applied).some((list) => list.length)) return null;
	return { applied, terrain: terrainOf(lines.rows, lines.cuts, lines.corners, lines.borders), d: lines.d };
}

function sceneWrite(attrs: Values, local: Values = {}): Write {
	return { type: "scene", attrs, local };
}

function patches(before: string[], after: string[]): CellPatch[] {
	const cells: CellPatch[] = [];
	for (let row = 0; row < Math.max(before.length, after.length); row++) {
		for (let col = 0; col < Math.max(before[row]?.length ?? 0, after[row]?.length ?? 0); col++) {
			const [from, to] = [codeAt(before, col, row), codeAt(after, col, row)];
			if (from !== to) cells.push([col, row, from, to]);
		}
	}
	return cells;
}

// Lays each cell's target code where it still holds the code it is taken
// from; null when no cell does.
function applyPatches(rows: string[], cells: CellPatch[], undo: boolean) {
	const next = [...rows];
	const applied = cells.filter(([col, row, before, after]) => {
		if (codeAt(next, col, row) !== (undo ? after : before)) return false;
		layCell(next, col, row, undo ? before : after);
		return true;
	});
	return applied.length ? { rows: normalizeRows(next), cells: applied } : null;
}

function codeAt(rows: string[], col: number, row: number) {
	return rows[row]?.[col] || ".";
}

function fogDefault(fog: Scene["fog"]) {
	return fog?.default === "revealed" ? "revealed" : "hidden";
}

function fogState(fog: Scene["fog"]): FogAll {
	return { default: fogDefault(fog), rows: normalizeRows(fogRows(fog)) };
}

function isEmpty(change: Change) {
	if (change.type === "terrain") return !change.cells.length;
	if (change.type === "fog") return !change.cells.length;
	if (change.type === "edges") return !change.n.length && !change.w.length && !change.d?.length && !change.cells?.length;
	return false;
}

// Client twins of the server's normalisers: rotation modulo 360, a blank
// label as nil, a text's folded whitespace, coordinates and points within
// 0.01 px, size, span and rotation only to a float's precision. A note's
// text keeps its line breaks, so it compares exactly.
function same(key: string, a: unknown, b: unknown, collection?: Collection): boolean {
	const [left, right] = [a ?? DEFAULTS[key] ?? null, b ?? DEFAULTS[key] ?? null];
	switch (key) {
		case "x":
		case "y":
			return close(left, right);
		case "rotation":
			return exact(0, ((((Number(left) - Number(right)) % 360) + 540) % 360) - 180);
		case "size":
		case "span":
			return exact(left ?? 1, right ?? 1);
		case "label":
			return blank(String(left ?? "").trim()) === blank(String(right ?? "").trim());
		case "text":
			return collection === "notes" ? String(left ?? "") === String(right ?? "") : blank(fold(left)) === blank(fold(right));
		case "points":
			return Array.isArray(left) && Array.isArray(right) && left.length === right.length && left.every((value, index) => close(value, right[index]));
		default:
			return left === right;
	}
}

function close(a: unknown, b: unknown) {
	return Math.abs(Number(a) - Number(b)) <= EPSILON;
}

// The server keeps these as written, so they match only within a numerical
// tolerance for floating-point noise, and any real edit is a change.
function exact(a: unknown, b: unknown) {
	return Math.abs(Number(a) - Number(b)) <= 1e-9;
}

function blank(text: string) {
	return text || null;
}

function fold(text: unknown) {
	return String(text ?? "")
		.replace(/\s+/g, " ")
		.trim();
}
